import { APPOINTMENT_STATUS, OFFICER_AVAILABILITY, type LifecycleEntry } from '@ministry/ui/internal';

/**
 * Reconciles `@ministry/ui`'s `APPOINTMENT_STATUS`/`OFFICER_AVAILABILITY` vocabularies
 * (designed for the PUBLIC portal's coarser view) against the full internal status set the
 * running server actually returns — confirmed live by driving the accept → reschedule →
 * decline → cancel → verify → complete flow end to end against seeded appointments.
 *
 * `APPOINTMENT_STATUS` is missing two codes an internal officer/front-desk screen must
 * render: `arrived` (set by `POST /appointments/verify` itself, per AC-26) and
 * `cancelled` (`POST /appointments/{id}/cancel`) — neither is a citizen-visible distinction
 * `web/public` needed, which is presumably why they were never added there. Rather than
 * widen the shared design-system vocabulary unreviewed, this file adds the two missing
 * entries locally, the same "verified against a running server, gap documented, not
 * silently patched upstream" pattern `file-lifecycle.ts` already uses for confidentiality
 * levels and reassignment reasons.
 */
const ARRIVED_ENTRY: LifecycleEntry = { token: 'info', icon: 'user-check', en: 'Arrived', ne: 'आइपुगेको' };
const CANCELLED_ENTRY: LifecycleEntry = { token: 'neutral', icon: 'x-circle', en: 'Cancelled', ne: 'रद्द' };

const EXTRA_APPOINTMENT_STATUS: Record<string, LifecycleEntry> = {
  arrived: ARRIVED_ENTRY,
  cancelled: CANCELLED_ENTRY,
};

/** Never throws: an appointment status this bundle does not recognise renders as a neutral, honestly-labelled badge using the server's own bilingual label, never a blank or a crash. */
export function appointmentStatusEntry(status: { code: string; labelNe?: string; labelEn?: string }): LifecycleEntry {
  if (status.code in APPOINTMENT_STATUS) {
    return APPOINTMENT_STATUS[status.code as keyof typeof APPOINTMENT_STATUS];
  }
  if (status.code in EXTRA_APPOINTMENT_STATUS) return EXTRA_APPOINTMENT_STATUS[status.code] as LifecycleEntry;
  return { token: 'neutral', icon: 'clock', en: status.labelEn ?? status.code, ne: status.labelNe ?? status.code };
}

/**
 * `OFFICER_AVAILABILITY`'s keys (`available | busy | inMeeting | onLeave | outOfOffice`)
 * do not match the server's `availability_status.code` values
 * (`available | in_meeting | on_leave | out_of_office | not_published`) at all except
 * `available` — confirmed live via `GET /me/availability`. Mapped explicitly rather than
 * assumed equal.
 */
const AVAILABILITY_CODE_TO_ENTRY: Record<string, LifecycleEntry> = {
  available: OFFICER_AVAILABILITY.available,
  in_meeting: OFFICER_AVAILABILITY.inMeeting,
  on_leave: OFFICER_AVAILABILITY.onLeave,
  out_of_office: OFFICER_AVAILABILITY.outOfOffice,
  not_published: { token: 'neutral', icon: 'help-circle', en: 'Not set', ne: 'तोकिएको छैन' },
};

export function availabilityStatusEntry(statusCode: string, labelNe?: string, labelEn?: string): LifecycleEntry {
  const entry = AVAILABILITY_CODE_TO_ENTRY[statusCode];
  if (entry) return entry;
  return { token: 'neutral', icon: 'help-circle', en: labelEn ?? statusCode, ne: labelNe ?? statusCode };
}
